import React from "react";
import { HiCalendarDays, HiTrophy, HiXMark } from "react-icons/hi2";
import { SessionVM } from "./scheduleTypes";
import { getSessionState, SessionStateKey } from "./scheduleGrouping";
import { resolveTrainingTypeColor, ColorMap } from "../../utils/scheduleColors";

// Peak Design System (Figma 2Xgaeen6KRSnprkmMAc2Fy): no schedule-card frame
// existed at the time of writing; the card uses DaisyUI semantic tokens,
// font-grotesk labels, font-geist values and rounded-xl so the Programs view
// (plan 3g) can reuse it. Record the node id here once designed.

interface SessionCardProps {
  session: SessionVM;
  trainingTypeColors?: ColorMap;
  /**
   * "list" shows the date subtitle; "compact" omits it (grouped list, week
   * and day rows where the date is already the heading).
   */
  variant?: "list" | "compact";
  onClick?: () => void;
  onDelete?: () => void;
  /** Shown as a "Change date" affordance when the session may move. */
  onChangeDate?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  dragging?: boolean;
}

const formatDateSubtitle = (plannedDate: string): string => {
  const date = new Date(plannedDate + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compare = new Date(date);
  compare.setHours(0, 0, 0, 0);
  const dayLabel = date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const diffDays = Math.round(
    (compare.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays === 0) return `Today · ${dayLabel}`;
  if (diffDays === 1) return `Tomorrow · ${dayLabel}`;
  return dayLabel;
};

// Parity with iOS/Android (plan 3f): Logged = success, To log / Live =
// warning, Planned = neutral, Proposed = info, Rejected = error. Semantic
// tokens only so dark mode stays readable.
const STATE_CHIP_CLASS: Record<SessionStateKey, string> = {
  live: "bg-warning/15 text-warning",
  logged: "bg-success/15 text-success",
  toLog: "bg-warning/15 text-warning",
  planned: "bg-base-content/10 text-base-content/70",
  proposed: "bg-info/15 text-info",
  rejected: "bg-error/10 text-error",
};

export const SessionStateChip: React.FC<{
  session: Pick<SessionVM, "status" | "linkedTrainingLogID">;
  className?: string;
}> = ({ session, className = "" }) => {
  const state = getSessionState(session);
  return (
    <span
      data-testid="session-state"
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk ${STATE_CHIP_CLASS[state.key]} ${className}`}
    >
      {state.label}
    </span>
  );
};

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  trainingTypeColors,
  variant = "list",
  onClick,
  onDelete,
  onChangeDate,
  selectable = false,
  selected = false,
  onToggleSelect,
  draggable = false,
  onDragStart,
  onDragEnd,
  dragging = false,
}) => {
  const typeColor = resolveTrainingTypeColor(
    session.typeColorKey,
    trainingTypeColors,
  );

  const subtitle =
    session.status === "executing"
      ? "In progress"
      : formatDateSubtitle(session.plannedDate);

  const setLabel = `${session.exerciseCount} ${
    session.exerciseCount === 1 ? "set" : "sets"
  }`;
  const showProgress =
    session.completedCount > 0 && session.completedCount < session.exerciseCount;

  return (
    <div
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group relative flex items-start gap-3 rounded-xl bg-base-100 border p-4 transition-all ${
        selected
          ? "border-base-content ring-1 ring-base-content bg-base-300"
          : "border-base-300 hover:shadow-md"
      } ${draggable ? "cursor-grab active:cursor-grabbing" : onClick ? "cursor-pointer" : ""} ${
        dragging ? "opacity-50" : ""
      }`}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          onClick={(e) => e.stopPropagation()}
          className="checkbox checkbox-sm mt-0.5 flex-shrink-0"
        />
      )}

      {/* Type colour */}
      <span
        className="mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: typeColor }}
        aria-hidden
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-base-content font-grotesk truncate">
            {session.title}
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {session.timeLabel && !session.isLive && (
              <span className="text-sm text-base-content/60 font-geist tabular-nums">
                {session.timeLabel}
              </span>
            )}
            <SessionStateChip session={session} />
            {onDelete && !selectable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="flex h-9 w-9 -m-2 items-center justify-center rounded-xl text-base-content/25 hover:text-error hover:bg-error/10 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                aria-label="Delete session"
              >
                <HiXMark className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-grotesk">
          {session.typeName && (
            <span className="font-medium" style={{ color: typeColor }}>
              {session.typeName}
            </span>
          )}
          {session.typeName && <span className="text-base-content/30">·</span>}
          <span className="text-base-content/60 font-geist tabular-nums">
            {setLabel}
            {showProgress && (
              <span className="text-base-content/40">
                {" "}
                · {session.completedCount} done
              </span>
            )}
          </span>
        </div>

        {variant === "list" && (
          <p className="mt-1 text-sm text-base-content/50 font-grotesk">
            {subtitle}
          </p>
        )}

        {(session.programName ||
          session.eventName ||
          (session.coachIssued && session.coachName)) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/60 font-grotesk">
            {session.eventName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary max-w-[60vw] sm:max-w-xs truncate">
                <HiTrophy className="w-3 h-3 flex-shrink-0" />
                {session.eventName}
              </span>
            )}
            {session.programName && (
              <span className="inline-flex items-center rounded-full bg-base-content/10 px-2 py-0.5 text-[10px] font-medium max-w-[60vw] sm:max-w-xs truncate">
                {session.programName}
              </span>
            )}
            {session.coachIssued && session.coachName && (
              <span>Assigned by @{session.coachName}</span>
            )}
          </div>
        )}

        {onChangeDate && !selectable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChangeDate();
            }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-base-300 px-3 py-2 min-h-9 text-xs font-medium text-base-content/70 hover:bg-base-200 hover:text-base-content transition-colors font-grotesk"
          >
            <HiCalendarDays className="w-3.5 h-3.5" />
            Change date
          </button>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
